import type { PlanFeatureLimit } from '@/services/auth/types';
import { isEmptyOrEmptyObject } from './objects';
import { addDays, getUnixTime, isBefore } from 'date-fns';

export const getLimitValue = (feature: PlanFeatureLimit | Record<string, never> | undefined) => {
  // if the feature is an empty object it means there are no limits for this feature
  if (!isEmptyOrEmptyObject(feature)) {
    const featureLimits = feature as PlanFeatureLimit;
    return featureLimits.limit;
  }
  return false;
};

// Ported from the Ember `app/utils/planFeatures.ts` helpers, using date-fns instead of moment.
// A time-limited feature carries a `start_at` and a number of `days`; it has expired once
// `start_at + days` is in the past.
export const isExpired = (feature: PlanFeatureLimit | Record<string, never> | undefined) => {
  if (!isEmptyOrEmptyObject(feature)) {
    const featureLimits = feature as PlanFeatureLimit;
    if (featureLimits.start_at != null && featureLimits.days != null) {
      const expiresAt = addDays(new Date(featureLimits.start_at), featureLimits.days);
      return isBefore(expiresAt, new Date());
    }
  }

  return false;
};

export const getExpiresAt = (feature: PlanFeatureLimit | Record<string, never> | undefined) => {
  if (isEmptyOrEmptyObject(feature)) {
    return null;
  }

  const featureLimits = feature as PlanFeatureLimit;
  const { days } = featureLimits;
  // Matches the Ember helper's `!feature.days`: no expiry when days is missing or zero.
  if (days == null || days === 0) {
    return null;
  }

  // Matches the Ember helper: a missing `start_at` falls back to "now" (moment(undefined)).
  const startAt = featureLimits.start_at ?? Date.now();
  return getUnixTime(addDays(new Date(startAt), days));
};
